import { DashboardCell, DashboardConfig, DashboardLayout, DashboardRow } from '../types/gotypes'

/**
 * Pure structural mutations for dashboard layout — shared by BOTH surfaces
 * that let a user arrange widgets: the template builder (#971) and the
 * dashboard editor's drag-and-drop edit mode.
 *
 * Neither surface introduces a new layout primitive — both map drag-and-drop
 * directly onto the existing Page -> Row -> Cell model the rest of the
 * dashboard system already uses (see docs/dashboards.md "DashboardConfig
 * Structure"). Dropping a widget "beside" another just adds a cell to that
 * row (a "column" in user language); dropping "above/below" inserts a new
 * row. A nested `layout` cell — and its successor the `report_pillar` (#1080)
 * — is an ordinary widget as far as this file is concerned: it moves, resizes
 * and duplicates like any other, and nothing here ever reaches inside one.
 *
 * Every function here is pure: given a `DashboardLayout`, return a new one.
 * No React, no network, no ids beyond what's needed to place a cell.
 */

// #1116: there is no cell cap on user-driven placement. Drag and drop
// overrules predefined layouts, every single time — a predefined layout is a
// starter, not a rule. The former MAX_ROW_CELLS = 3 spill-to-new-row behaviour
// is gone; MIN_CELL_WIDTH_PCT remains the only structural constraint (a
// legibility floor for width DRAGS, not a placement rule — whether it should
// also bound cell count is an open question with Matt on #1116).

/**
 * Per-widget-type minimum height, in CSS pixels — the floor below which a
 * widget stops being legible. Dragging a row's bottom edge can never squeeze a
 * widget under its floor; a row's floor is the tallest floor among its widgets
 * (`rowMinHeight`). Keyed by `widget_type`, so `chart` and `table` share the
 * `table_graph` floor.
 *
 * These are deliberately in one exported map so they can be tuned in one place
 * as real dashboards show what is too tight.
 */
export const WIDGET_MIN_HEIGHTS: Record<string, number> = {
  table_graph: 200,
  multi_table_analysis: 160,
  report_pillar: 240,
  layout: 240,
  agent_artifacts: 200,
  video: 180,
  toc: 120,
  image: 120,
  callout: 100,
  kpi_card: 100,
  title_hero: 60,
  text: 60,
  // #1187: the Spacer's whole purpose is a LOW floor — it must never inflate
  // its row (row height = tallest widget's floor since #1177), and alone in a
  // row it gives a small, deliberate band of breathing room.
  spacer: 24,
}

/** Floor for a widget type with no entry of its own. */
export const DEFAULT_WIDGET_MIN_HEIGHT = 80

/** Narrowest a single cell may be dragged to, as a percentage of its row. */
export const MIN_CELL_WIDTH_PCT = 10

/**
 * MUI breakpoint below which a row's cells stack into one column, in source
 * order. Deliberately the only responsive rule: no reflow, no reordering.
 */
export const STACK_BREAKPOINT = 'md' as const

export interface CellRef {
  rowIndex: number
  cellIndex: number
}

/** Where a drag ended, expressed purely in terms of the layout, not pixels. */
export type DropTarget =
  /** Insert a new row at `index` (0..rows.length). */
  | { kind: 'row-gap'; index: number }
  /** Insert a new cell into the row at `rowIndex`, at `index` (0..cells.length). */
  | { kind: 'cell-gap'; rowIndex: number; index: number }

/**
 * The cell a widget type gets when dropped from the palette with no
 * configuration yet — a placeholder. Deliberately minimal: just enough shape
 * for `widget_type` to be meaningful and for the appropriate wizard to open
 * from the Edit Cell flow afterwards. `job` seeds job-scoped widget data so a
 * dashboard created from the builder never needs a job picker later (#961 —
 * the dashboard is locked to its job from creation).
 */
export function createPlaceholderCell(widgetType: string, job: string): DashboardCell {
  switch (widgetType) {
    case 'chart':
      return { widget_type: 'table_graph', table_graph_data: { job, table_path: '', view_mode: 'graph', chart_type: 'line', data_type: 'colPct' } }
    case 'table':
      return { widget_type: 'table_graph', table_graph_data: { job, table_path: '', view_mode: 'table' } }
    case 'text':
      return { widget_type: 'text', text_data: { content: '' } }
    case 'title_hero':
      return { widget_type: 'title_hero', title_hero_data: { heading: '', size: 'standard', alignment: 'center', bg_color: '#1565c0', text_color: '#ffffff' } }
    case 'callout':
      return { widget_type: 'callout', callout_data: { title: '', body: '' } }
    case 'multi_table_analysis':
      return { widget_type: 'multi_table_analysis', multi_table_analysis_data: { multi_table_id: '', job, persona: 'Executive Summarizer' } }
    case 'agent_artifacts':
      return { widget_type: 'agent_artifacts', agent_artifact_data: { session_id: '', layout: 'grid', max_items: 6 } }
    case 'kpi_card':
      return { widget_type: 'kpi_card', kpi_card_data: { value: '', label: '' } }
    case 'image':
      return { widget_type: 'image', image_data: { job, file_path: '' } }
    case 'video':
      return { widget_type: 'video', video_data: { job, file_path: '' } }
    case 'toc':
      return { widget_type: 'toc', toc_data: { mode: 'auto' } }
    case 'spacer':
      // #1187: deliberately no data payload — a spacer IS its empty space.
      return { widget_type: 'spacer' }
    default:
      return { widget_type: 'text', text_data: { content: '' } }
  }
}

function clearWidths(cells: DashboardCell[]): DashboardCell[] {
  return cells.map(cell => (
    cell.display_props?.width === undefined
      ? cell
      : { ...cell, display_props: { ...cell.display_props, width: undefined } }
  ))
}

/**
 * Drop a new widget from the palette at `target`. A `cell-gap` drop lands in
 * the row it was aimed at, at any position — there is no cell cap and no
 * spill-to-new-row fallback (#1116). `row-gap` drops create rows, unchanged.
 */
export function insertWidgetAtDropTarget(
  layout: DashboardLayout,
  target: DropTarget,
  widgetType: string,
  job: string
): DashboardLayout {
  const cell = createPlaceholderCell(widgetType, job)

  if (target.kind === 'row-gap') {
    const rows = [...layout.rows]
    rows.splice(target.index, 0, { cells: [cell] })
    return { ...layout, rows }
  }

  // cell-gap
  const rows = [...layout.rows]
  const row = rows[target.rowIndex]
  const cells = clearWidths(row.cells)
  cells.splice(target.index, 0, cell)
  rows[target.rowIndex] = { ...row, cells }
  return { ...layout, rows }
}

/**
 * Where the cell placed by `insertWidgetAtDropTarget` will end up.
 *
 * The editor needs this to open the new widget's editor straight after the
 * drop. Computed from the layout as it is BEFORE the insert. (#1116: no
 * full-row spill any more — a cell-gap drop lands exactly where it was aimed.)
 */
export function dropTargetRef(layout: DashboardLayout, target: DropTarget): CellRef {
  if (target.kind === 'row-gap') {
    return { rowIndex: clamp(target.index, 0, layout.rows.length), cellIndex: 0 }
  }
  const cells = layout.rows[target.rowIndex].cells
  return { rowIndex: target.rowIndex, cellIndex: clamp(target.index, 0, cells.length) }
}

/** The floor for one widget — see WIDGET_MIN_HEIGHTS. */
export function minHeightForCell(cell: DashboardCell): number {
  return WIDGET_MIN_HEIGHTS[cell.widget_type] ?? DEFAULT_WIDGET_MIN_HEIGHT
}

/**
 * The floor for a whole row: the tallest floor among its widgets. A row can
 * never be dragged shorter than this, which is what stops a height drag from
 * squeezing a chart into illegibility.
 */
export function rowMinHeight(row: DashboardRow): number {
  if (row.cells.length === 0) return DEFAULT_WIDGET_MIN_HEIGHT
  return Math.max(...row.cells.map(minHeightForCell))
}

/**
 * The height a row should actually render at: any stored height, but never
 * below the row's floor. Widgets fill this height (fit-to-cell), which is why
 * the row's box and its floor are one number rather than a per-widget setting.
 *
 * This is a MINIMUM, not a clamp: content taller than it grows the row rather
 * than being clipped or scrolled away.
 *
 * The row height drag was removed (#1177) — nothing writes
 * `display_props.height` any more — but rows saved before that may still carry
 * one, and it must keep shaping the render so those dashboards do not change
 * appearance.
 */
export function resolveRowHeight(row: DashboardRow): number {
  return Math.max(row.display_props?.height ?? 0, rowMinHeight(row))
}

/**
 * Move the boundary between cells `boundaryIndex` and `boundaryIndex + 1` by
 * `deltaPct`, taking from one and giving to the other. Only those two cells
 * change, so the row still sums to what it did, and neither side can be pushed
 * below `MIN_CELL_WIDTH_PCT`.
 */
export function redistributeWidths(
  widths: number[],
  boundaryIndex: number,
  deltaPct: number,
  minPct: number = MIN_CELL_WIDTH_PCT
): number[] {
  const left = widths[boundaryIndex]
  const right = widths[boundaryIndex + 1]
  if (left === undefined || right === undefined) return widths
  const pair = left + right
  // Both sides need `minPct`, so an already-cramped pair can only stay put.
  if (pair < minPct * 2) return widths
  const nextLeft = Math.round(clamp(left + deltaPct, minPct, pair - minPct))
  const next = [...widths]
  next[boundaryIndex] = nextLeft
  next[boundaryIndex + 1] = pair - nextLeft
  return next
}

/** Equal widths for `count` cells, with the rounding remainder on the last. */
export function distributeWidthsEvenly(count: number): number[] {
  if (count <= 0) return []
  const base = Math.floor(100 / count)
  const widths = new Array(count).fill(base)
  widths[count - 1] = 100 - base * (count - 1)
  return widths
}

/** Write explicit widths onto a row's cells. */
export function setRowWidths(layout: DashboardLayout, rowIndex: number, widths: number[]): DashboardLayout {
  const row = layout.rows[rowIndex]
  if (!row) return layout
  const rows = [...layout.rows]
  rows[rowIndex] = {
    ...row,
    cells: row.cells.map((cell, i) => ({ ...cell, display_props: { ...cell.display_props, width: widths[i] } })),
  }
  return { ...layout, rows }
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max)
}

/**
 * Remove the widget at `ref`. If its row becomes empty, the row is removed
 * too — an empty row has no meaning in this model (mirrors
 * `DashboardEditor.handleDeleteCell`).
 */
export function removeWidgetAt(layout: DashboardLayout, ref: CellRef): DashboardLayout {
  const row = layout.rows[ref.rowIndex]
  if (!row) return layout
  const cells = clearWidths(row.cells.filter((_, i) => i !== ref.cellIndex))
  if (cells.length === 0) {
    return { ...layout, rows: layout.rows.filter((_, i) => i !== ref.rowIndex) }
  }
  const rows = [...layout.rows]
  rows[ref.rowIndex] = { ...row, cells }
  return { ...layout, rows }
}

/** Adjust a drop target's indices for the fact that `source` is about to be removed. */
function adjustTargetForRemoval(target: DropTarget, source: CellRef): DropTarget {
  if (target.kind === 'row-gap') {
    // Removing source's row (if it becomes empty) shifts every row after it
    // up by one. We only know for certain it becomes empty if it currently
    // has exactly one cell — otherwise the row survives and no shift happens.
    return target
  }
  if (target.rowIndex === source.rowIndex && target.index > source.cellIndex) {
    return { ...target, index: target.index - 1 }
  }
  return target
}

/**
 * Move an existing widget to a new location.
 *
 * A reorder WITHIN a row is a pure permutation: every cell — the moved one
 * included — keeps its `display_props.width`, per Matt's decision on #1111
 * (a 20%-wide widget dragged to first position stays 20% wide). Cross-row
 * moves stay remove-then-insert, which clears widths on both rows so each
 * redistributes evenly — a travelling width has no meaning against a
 * different row's total.
 */
export function moveWidgetToDropTarget(
  layout: DashboardLayout,
  source: CellRef,
  target: DropTarget
): DashboardLayout {
  const row = layout.rows[source.rowIndex]
  const cell = row?.cells[source.cellIndex]
  if (!cell) return layout

  if (target.kind === 'cell-gap' && target.rowIndex === source.rowIndex) {
    const cells = [...row.cells]
    const [moved] = cells.splice(source.cellIndex, 1)
    const insertAt = clamp(
      target.index > source.cellIndex ? target.index - 1 : target.index,
      0,
      cells.length
    )
    cells.splice(insertAt, 0, moved)
    const rows = [...layout.rows]
    rows[source.rowIndex] = { ...row, cells }
    return { ...layout, rows }
  }

  const rowBecomesEmpty = row.cells.length === 1
  const adjustedTarget: DropTarget = (() => {
    if (!rowBecomesEmpty) return adjustTargetForRemoval(target, source)
    // The whole row disappears, shifting every later row index up by one.
    if (target.kind === 'row-gap' && target.index > source.rowIndex) {
      return { ...target, index: target.index - 1 }
    }
    if (target.kind === 'cell-gap' && target.rowIndex > source.rowIndex) {
      return { ...target, rowIndex: target.rowIndex - 1 }
    }
    return target
  })()

  const withoutSource = removeWidgetAt(layout, source)
  // The travelling cell sheds its width too — its percentage was of the OLD
  // row's total. The target row's cells are cleared on insert, so the whole
  // destination row redistributes evenly.
  const [strippedCell] = clearWidths([cell])
  return insertWidgetAtDropTargetWithCell(withoutSource, adjustedTarget, strippedCell)
}

/** Like `insertWidgetAtDropTarget`, but for an existing cell rather than a fresh placeholder. */
export function insertWidgetAtDropTargetWithCell(layout: DashboardLayout, target: DropTarget, cell: DashboardCell): DashboardLayout {
  if (target.kind === 'row-gap') {
    const rows = [...layout.rows]
    const index = Math.max(0, Math.min(target.index, rows.length))
    rows.splice(index, 0, { cells: [cell] })
    return { ...layout, rows }
  }
  const rows = [...layout.rows]
  const row = rows[target.rowIndex]
  const cells = clearWidths(row.cells)
  const index = Math.max(0, Math.min(target.index, cells.length))
  cells.splice(index, 0, cell)
  rows[target.rowIndex] = { ...row, cells }
  return { ...layout, rows }
}

/**
 * Duplicate the widget at `ref` into the same row, immediately after it
 * (#1116: always in the same row — the full-row spill is gone).
 */
export function duplicateWidgetAt(layout: DashboardLayout, ref: CellRef): DashboardLayout {
  const row = layout.rows[ref.rowIndex]
  const cell = row?.cells[ref.cellIndex]
  if (!cell) return layout
  const clone: DashboardCell = JSON.parse(JSON.stringify(cell))
  return insertWidgetAtDropTargetWithCell(layout, { kind: 'cell-gap', rowIndex: ref.rowIndex, index: ref.cellIndex + 1 }, clone)
}

/** An empty, single-row-less layout — the starting point for a blank canvas or a new tab. */
export function emptyLayout(): DashboardLayout {
  return { rows: [] }
}

/**
 * Re-stamp `job` into every widget in a config loaded from a personal/company
 * template (#971 Phase 2). The server's `TemplateFromDashboard` blanks every
 * widget's `job` field on save — templates are "reusable regardless of data
 * source" (#971) — so loading one into the builder needs the inverse step,
 * the same way a system template's `build(job)` injects the target job at
 * selection time (see systemTemplates.ts). Pure and job-only: it never
 * touches table_path/multi_table_id/etc, which stay empty until the user
 * configures each widget.
 */
export function stampTemplateJob(config: DashboardConfig, job: string): DashboardConfig {
  return {
    ...config,
    layout: stampLayoutJob(config.layout, job),
    pages: config.pages?.map(page => ({ ...page, layout: stampLayoutJob(page.layout, job) })),
  }
}

function stampLayoutJob(layout: DashboardLayout, job: string): DashboardLayout {
  return { ...layout, rows: layout.rows.map(row => ({ ...row, cells: row.cells.map(cell => stampCellJob(cell, job)) })) }
}

function stampCellJob(cell: DashboardCell, job: string): DashboardCell {
  const next: DashboardCell = { ...cell }
  if (next.table_graph_data) next.table_graph_data = { ...next.table_graph_data, job }
  if (next.multi_table_analysis_data) next.multi_table_analysis_data = { ...next.multi_table_analysis_data, job }
  if (next.image_data) next.image_data = { ...next.image_data, job }
  if (next.video_data) next.video_data = { ...next.video_data, job }
  if (next.layout_data) next.layout_data = stampLayoutJob(next.layout_data, job)
  return next
}

/** Type guard so callers can branch on DropTarget without a `default` case going stale. */
export function isSameCell(a: CellRef, b: CellRef): boolean {
  return a.rowIndex === b.rowIndex && a.cellIndex === b.cellIndex
}

export type { DashboardLayout, DashboardRow, DashboardCell }
